/**
 * NewDashboard — web Pass 2+ (#320 / #322) of the redesign initiative.
 *
 * Mirrors the iOS NewTodayView (#308) DNA on desktop while preserving
 * EVERY feature of the legacy cream-light dashboard so studios don't
 * lose anything when they flip the dark cinema toggle on:
 *
 *   • Status bar — greeting + date + Live pill (new chrome)
 *   • Shortcut tiles — New project / Add lead / Log expense /
 *     Ask Nova / Calendar (new — was the user's #1 ask in #322)
 *   • Hero rollup card — 3 colored stat columns (new chrome)
 *   • StudioRings — activity rings (legacy, reused)
 *   • This Week strip (legacy, dark-scoped)
 *   • Pinned goals card (legacy, dark-scoped)
 *   • Mini analytics bar (legacy, dark-scoped)
 *   • Activation card (legacy, dark-scoped)
 *   • Pipeline: ServiceTabs + StatsRow + SearchFilters + InquiryTable
 *     (legacy, dark-scoped — this is the core CRM the user lives in)
 *   • RecentProjects / AnalyticsOverview / SummaryPanels (legacy)
 *
 * Dark treatment for the legacy widgets is applied via Tailwind
 * arbitrary-property scope overrides on the wrapper (`[&_.bg-card]`,
 * etc.) — same pattern used in NewSettings.tsx. This lets us inherit
 * EVERY feature/behavior of the existing components without forking
 * them, while still painting in the dark Oura DNA palette.
 *
 * Data hooks are duplicated with the legacy Index.tsx render path on
 * purpose — TanStack Query dedupes them under the hood, and the
 * duplication keeps the two render branches independent so we can
 * iterate on the new shell without disturbing the cream-light one.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  UserPlus,
  Receipt,
  MessageSquare,
  Calendar as CalendarIcon,
  Images,
  Inbox,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useOrganization } from '@/services/organizations';
import { useInquiryCategories } from '@/services/inquiryCategories';
import { useClients, useClientsRealtime } from '@/services/clients';
import { useProjects, useProjectsRealtime } from '@/services/projects';
import { useGoals, useGoalsRealtime } from '@/services/passport';
import {
  useInquiries,
  useDeleteInquiry,
  useInquiryStats,
  useAnalyticsData,
} from '@/services/inquiries';
import { useToast } from '@/hooks/use-toast';
import { ServiceType, Inquiry, InquiryStatus, ServiceSummary } from '@/types/crm';
import {
  ServiceTabConfig,
  getDefaultServiceTabs,
} from '@/data/serviceTabPresets';

// Existing dashboard widgets — reused as-is inside dark glass wrappers.
import { ServiceTabs } from '@/components/crm/ServiceTabs';
import { StatsRow } from '@/components/crm/StatsRow';
import { SearchFilters } from '@/components/crm/SearchFilters';
import { InquiryTable } from '@/components/crm/InquiryTable';
import { InquiryModal } from '@/components/crm/InquiryModal';
import { ManualEntryModal } from '@/components/crm/ManualEntryModal';
// HOTFIX-2 — PostgrestError isn't an Error instance; parseSupabaseError
// is the only safe way to read a Supabase failure message.
import debug from '@/lib/debug';
import { AnalyticsOverview } from '@/components/crm/AnalyticsOverview';
import { SummaryPanels } from '@/components/crm/SummaryPanels';
import { ActivationCard } from '@/components/dashboard/ActivationCard';
import { PassportTodayCard } from '@/components/dashboard/PassportTodayCard';
// SUB-1 Phase 3B — compact storage widget on Today. Only rendered
// when the studio is at or over 75% of their cap so the dashboard
// stays quiet for paid studios with plenty of runway.
import { StorageUsageBar } from '@/components/dashboard/StorageUsageBar';
import { useUsageSnapshot } from '@/hooks/useUsageSnapshot';
// Wave 10 — MiniAnalyticsBar removed. Its four tiles (Inquiries
// today / Projects active / Contracts sent / Payments received)
// duplicated ThisWeekStrip's stats and added a second row of
// colored chips that made Today feel busy.
import { ThisWeekStrip } from '@/components/dashboard/ThisWeekStrip';
import { PinnedGoalsCard } from '@/components/dashboard/PinnedGoalsCard';
import RecentProjects from '@/components/dashboard/RecentProjects';
import StudioRings from '@/components/dashboard/StudioRings';
import CreateProjectModal from '@/components/projects/CreateProjectModal';
// POL-2 — reuses the standalone gallery uploader that Galleries.tsx
// mounts.  Extracted from the Galleries page so Today can trigger the
// same flow (photos → Nova project-match → create + upload) without a
// forked form.
import { NewGalleryDialog } from '@/components/gallery/NewGalleryDialog';
import { useGamification } from '@/hooks/useGamification';
import { EmptyState } from '@/components/shared/EmptyState';
import { SkeletonStats, SkeletonTable } from '@/components/shared/SkeletonCard';
// POL-10 — one unified sky for the whole Today route.  The old
// boxed hero backdrop (TodayHeroBackdrop) was retired; moon +
// varied star shapes now ride the page-wide backdrop, and the
// greeting text below floats over that sky with no card chrome
// of its own.  See TodayPageBackdrop for gradient buckets, star
// shapes and lunar-phase math; the iOS parity lives in NewTodayView.
import { TodayPageBackdrop } from '@/components/dashboard/TodayPageBackdrop';
// Wave 13a — darkScope gated on the theme mode so light mode
// gets a clean Mission Control pass-through instead of white-on-white.
import { useDarkScopeClass, useThemeMode } from '@/hooks/useThemeMode';
// POL-16 — Nova rides inside the Ask Nova shortcut tile, her body
// tinted to the CURRENT time-of-day sky color so she reads as part
// of the backdrop (Rennan: "color match her body to whatever the
// background is"). Same palette source as TodayPageBackdrop.
import { NovaAvatar, shadeHex } from '@/components/nova/kit';
import { bucketFromHour, gradientFor } from '@/lib/todayTimeOfDay';
// POL-16 — in Aperture mode RootLayout pads the page column down by
// md:pt-20 so the fixed brand mark doesn't overlap headings, which
// left an 80px bare-black band above the Today sky. We read the nav
// style so Today can pull its backdrop up under that band (negative
// margin) and re-apply the padding to the CONTENT column only.
import { useNavStyle } from '@/hooks/useNavStyle';

export default function NewDashboard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { data: organization } = useOrganization();
  const { data: inquiryCategories = [] } = useInquiryCategories();
  const [gamificationOn] = useGamification();
  const { data: allGoals = [] } = useGoals();

  // Open goal presence — gates whether the Passport capsule replaces
  // StudioRings on Today. When gamification is off OR there is no
  // open goal, we fall back to the legacy generic rings.
  const hasActivePlanet = allGoals.some((g) => !g.completed_at);
  const showHeroPlanet = gamificationOn && hasActivePlanet;

  // SIGNUP-5 — the earned-planet celebration modal (PP-1a) is retired
  // on Today: PassportTodayCard shows the pending planet as
  // "reachable" and its Launch button plays the arrival sequence,
  // then stamps `celebration_seen_at` itself.  Galaxy keeps its own.

  // Realtime subscriptions — match legacy dashboard so cross-device
  // updates flow into both render paths.
  useClientsRealtime();
  useProjectsRealtime();
  useGoalsRealtime();

  // Service tab config — same fallback chain as legacy Index.tsx.
  const serviceTabs: ServiceTabConfig[] = useMemo(() => {
    const allTab: ServiceTabConfig = { id: 'all', label: 'All Inquiries', icon: 'inbox' };
    if (inquiryCategories.length > 0) {
      return [
        allTab,
        ...inquiryCategories.map((cat) => ({
          id: cat.name.trim().toLowerCase().replace(/\s+/g, '_'),
          label: cat.name,
          icon: cat.icon || 'sparkles',
        })),
      ];
    }
    if (
      organization?.service_tabs &&
      Array.isArray(organization.service_tabs) &&
      organization.service_tabs.length > 0
    ) {
      return [allTab, ...(organization.service_tabs as ServiceTabConfig[])];
    }
    return [allTab, ...getDefaultServiceTabs()];
  }, [inquiryCategories, organization]);

  // Pipeline state — mirrors legacy Index.tsx.
  const [activeTab, setActiveTab] = useState<ServiceType>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<InquiryStatus | 'all'>('all');
  const [portalsFilter, setPortalsFilter] = useState(false);
  const [activeStatFilter, setActiveStatFilter] =
    useState<InquiryStatus | 'all' | 'portals'>('all');
  const [selectedInquiry, setSelectedInquiry] = useState<Inquiry | null>(null);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [createProjectOpen, setCreateProjectOpen] = useState(false);
  // POL-2 — Today → new gallery. Mounts the same NewGalleryDialog
  // Galleries.tsx uses; on success the dialog navigates to the
  // project workspace's Galleries tab (see NewGalleryDialog).
  const [newGalleryOpen, setNewGalleryOpen] = useState(false);

  useEffect(() => {
    if (serviceTabs.length > 0 && !activeTab) {
      setActiveTab('all');
    }
  }, [serviceTabs, activeTab]);

  // Data hooks.
  const { data: inquiries = [], isLoading: loadingInquiries } = useInquiries();
  const { data: clients = [] } = useClients();
  const { data: projects = [] } = useProjects();
  const deleteInquiryMutation = useDeleteInquiry();
  const stats = useInquiryStats(activeTab);
  const analytics = useAnalyticsData();

  // Studio activity gate — show data-driven widgets only when there's
  // something meaningful to render.
  const hasStudioActivity = inquiries.length + projects.length > 0;

  // Map category_id → tab slug for tagged-client filtering.
  const categoryIdToSlug = useMemo(() => {
    const map: Record<string, string> = {};
    inquiryCategories.forEach((cat) => {
      map[cat.id] = cat.name.trim().toLowerCase().replace(/\s+/g, '_');
    });
    return map;
  }, [inquiryCategories]);

  // Tagged clients become inquiry-like rows for the pipeline merge.
  const clientAsInquiries = useMemo<Inquiry[]>(() => {
    return clients
      .filter((c) => c.category_id)
      .map((c) => ({
        id: `client-${c.id}`,
        name: c.name,
        email: c.email || '',
        phone: c.phone || undefined,
        interest:
          inquiryCategories
            .find((cat) => cat.id === c.category_id)
            ?.name?.trim() || 'Client',
        budget: '$0',
        status: 'accepted' as InquiryStatus,
        projectStage: 'lead' as any,
        clientPortalEnabled: false,
        submittedAt:
          (c as any).created_at || (c as any).createdAt || new Date().toISOString(),
        serviceType: c.category_id
          ? categoryIdToSlug[c.category_id] || 'general'
          : 'general',
      }));
  }, [clients, categoryIdToSlug, inquiryCategories]);

  // Merge + filter — same recipe as legacy.
  const allEntries = useMemo(() => {
    const inquiryEmails = new Set(
      inquiries.map((i) => `${i.email}|${i.serviceType}`),
    );
    const uniqueClients = clientAsInquiries.filter(
      (c) => !inquiryEmails.has(`${c.email}|${c.serviceType}`),
    );
    return [...inquiries, ...uniqueClients];
  }, [inquiries, clientAsInquiries]);

  const summaries = useMemo(() => {
    const result: Record<string, ServiceSummary> = {};
    serviceTabs.forEach((tab) => {
      const tabEntries = allEntries.filter((i) => i.serviceType === tab.id);
      result[tab.id] = {
        total: tabEntries.length,
        accepted: tabEntries.filter((i) => i.status === 'accepted').length,
        inProgress: tabEntries.filter((i) => i.status === 'in_progress').length,
      };
    });
    return result;
  }, [allEntries, serviceTabs]);

  const filteredInquiries = useMemo(() => {
    return allEntries.filter((inquiry) => {
      const matchesTab = activeTab === 'all' || inquiry.serviceType === activeTab;
      const matchesSearch =
        inquiry.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        inquiry.email.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || inquiry.status === statusFilter;
      const matchesPortals = !portalsFilter || inquiry.clientPortalEnabled;
      return matchesTab && matchesSearch && matchesStatus && matchesPortals;
    });
  }, [allEntries, activeTab, searchQuery, statusFilter, portalsFilter]);

  // Wave 10 — Hero rollup (Inquiries 7D / Active Projects / Clients)
  // removed. Duplicated ThisWeekStrip and added visual noise; the
  // 3-stat vanity band was pushed by "editorial" but shipped as
  // dashboard chrome. ThisWeekStrip now owns the hero role.

  // Stat-card click — same routing as legacy.
  const handleStatClick = (filter: InquiryStatus | 'all' | 'portals') => {
    setActiveStatFilter(filter);
    if (filter === 'portals') {
      setStatusFilter('all');
      setPortalsFilter(true);
    } else if (filter === 'all') {
      setStatusFilter('all');
      setPortalsFilter(false);
    } else {
      setStatusFilter(filter);
      setPortalsFilter(false);
    }
  };

  // POL-16 — Export CSV removed from the pipeline chrome per Rennan.
  const handleUpdateInquiry = async () => setSelectedInquiry(null);

  const handleDeleteInquiry = async (id: string) => {
    try {
      await deleteInquiryMutation.mutateAsync(id);
      setSelectedInquiry(null);
      toast({
        title: 'Inquiry Deleted',
        description: 'The inquiry has been removed.',
        variant: 'destructive',
      });
    } catch (error) {
      // HOTFIX-2 — a bare catch threw the reason away. PostgrestError
      // is not an `Error` instance, so parse it properly.
      console.error('Delete inquiry failed:', error);
      toast({
        title: 'Error',
        description: `Failed to delete — ${debug.parseSupabaseError(error)}`,
        variant: 'destructive',
      });
    }
  };

  // Status-bar copy.
  const fullName: string =
    (user?.user_metadata as Record<string, string> | undefined)?.full_name || '';
  const firstName =
    fullName.split(' ')[0] || user?.email?.split('@')[0] || 'there';
  const hour = new Date().getHours();
  const timeOfDay =
    hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';
  const greeting = `Good ${timeOfDay}, ${firstName}`;
  const dayLabel = new Date()
    .toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    })
    .toUpperCase();

  // Shared dark-scope override classes for legacy widget wrappers.
  // Forces the legacy white/cream surfaces inside child components
  // onto the dark glass palette without forking each component.
  // Wave 13a — gated on the theme mode so light mode gets a
  // clean Mission Control pass-through instead of white-on-white.
  const darkScope = useDarkScopeClass();

  // POL-16 — Aperture mode: RootLayout's page wrapper carries
  // md:pt-20 (clearance for the fixed top-left brand mark), which
  // painted as an 80px bare-black bar above the sky. Cancel it with
  // a matching negative margin so TodayPageBackdrop bleeds up to the
  // viewport edge, then re-apply the same clearance on the inner
  // content column so the greeting still sits below the logo.
  const [navStyle] = useNavStyle();
  const isAperture = navStyle === 'aperture';

  // POL-16 — Nova's body color for the Ask Nova tile, matched to the
  // sky. Dark mode samples the time-of-day palette's mid stop (the
  // tiles sit roughly a fifth of the way down the gradient); light
  // mode uses a soft ivory that sits into the near-white canvas.
  const { mode: themeMode } = useThemeMode();
  const novaBody = useMemo(() => {
    if (themeMode === 'light') {
      return { body: '#ECEAE3', dark: '#DAD6CB' };
    }
    const g = gradientFor(bucketFromHour(new Date().getHours()));
    return { body: g.colors[1], dark: shadeHex(g.colors[1], -0.22) };
  }, [themeMode]);

  return (
    // POL-6 — outer relative wrapper hosts the page-wide time-of-day
    // backdrop.  `min-h-screen` guarantees the backdrop covers the
    // full scroll region even on short pages (fresh studios with
    // little pipeline data), so the charcoal floor lands under the
    // fold instead of the body's stale orange ambient peeking through.
    <div className={`relative min-h-screen ${isAperture ? 'md:-mt-20' : ''}`}>
      <TodayPageBackdrop />
      <div className={`relative z-10 mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 py-6 md:py-10 space-y-12 ${isAperture ? 'md:pt-20' : ''}`}>
      {/* ─── Greeting ───────────────────────────────────────────
          POL-10 — no more boxed hero.  Text just floats on the
          page-wide sky painted by TodayPageBackdrop.  The moon +
          starfield sit behind everything; the eyebrow + Fraunces
          greeting are the only foreground element in this band.
          No rounded rectangle, no border, no background — one
          sky, greeting on top. */}
      {/* POL-11e — greeting was hard-coded white with a dark drop
          shadow so it read on the dark sky.  In light mode Today
          renders on a plain near-white canvas (no sky), so we invert
          to `text-foreground` and drop the shadow.  `dark:` variants
          preserve the original dark-sky treatment. */}
      <header className="py-6 md:py-10">
        <p className="orbit-eyebrow text-foreground/70 dark:text-white/75">
          {dayLabel}
        </p>
        <h1 className="font-editorial text-4xl md:text-5xl text-foreground dark:text-white leading-none mt-2 dark:drop-shadow-[0_2px_16px_rgba(0,0,0,0.35)]">
          {greeting}.
        </h1>
      </header>

      {/* ─── Passport capsule — SIGNUP-5 ───────────────────────────
          The prototype's compact Passport card sits right under the
          greeting: the studio's world, the ring that needs love, and
          what fills it next. Gold + Launch when every ring is full;
          a stamp for the day after Nova lands. Gamification-gated,
          same as HeroPlanet. */}
      {gamificationOn && (
        <section className={darkScope}>
          <PassportTodayCard />
        </section>
      )}

      {/* ─── Shortcut tiles ───────────────────────────────────────
          Wave 10 — unified accent to `orbit-orange`.  The old
          orange/teal/mustard/blue/purple rainbow made the row feel
          like a launcher advert. One accent + neutral surface reads
          calmer.  Ask Nova's Sparkles icon swapped for MessageSquare
          (Sparkles is now reserved for Nova-owned surfaces).
          POL-2 — added "New gallery" so the studio can send a fresh
          set to a client without hopping to the Galleries tab. Icon
          matches the sidebar's Galleries entry (`Images`). */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <ShortcutTile
          icon={Plus}
          label="New project"
          tone="green"
          onClick={() => setCreateProjectOpen(true)}
        />
        <ShortcutTile
          icon={UserPlus}
          label="Add lead"
          tone="blue"
          onClick={() => setShowManualEntry(true)}
        />
        <ShortcutTile
          icon={Images}
          label="New gallery"
          tone="teal"
          onClick={() => setNewGalleryOpen(true)}
        />
        <ShortcutTile
          icon={Receipt}
          label="Log expense"
          tone="red"
          onClick={() => navigate('/expenses')}
        />
        <ShortcutTile
          icon={MessageSquare}
          label="Ask Nova"
          tone="pink"
          visual={
            <NovaAvatar
              width={44}
              bodyColor={novaBody.body}
              bodyDarkColor={novaBody.dark}
              antennaGlow
              className="shrink-0 -my-1"
            />
          }
          onClick={() => navigate('/assistant')}
        />
        <ShortcutTile
          icon={CalendarIcon}
          label="Calendar"
          tone="yellow"
          onClick={() => navigate('/calendar')}
        />
      </div>

      {/* ─── This week — hero band, full width ────────────────────
          Wave 10 — Promoted to the hero role. No more side-by-side
          with goals stealing the right column. */}
      <section>
        <ThisWeekStrip />
      </section>

      {/* ─── Pipeline (service tabs + stats + filters + table) ───
          Wave 10 — Full section break above (space-y-12 already sets
          the gap). Inner wrappers use plain neutral surfaces, no
          layered glass rings.
          UX-7 — promoted ABOVE the rings/goals/activation band. The
          pipeline is the working core of Today; it used to sit below
          greeting + tiles + ops strip + rings + a nine-row checklist,
          i.e. under the fold for exactly the people who live in it.
          Greeting, tiles and ThisWeekStrip stay on top (they tested
          well); everything decorative now reads below the work. */}
      <section className="space-y-4 pt-4">
        <div className="flex items-end justify-between gap-4">
          <div>
            <p className="orbit-eyebrow">
              PIPELINE
            </p>
            {/* #494 Pass 2 — Section title → Fraunces italic. */}
            <h2 className="font-editorial text-3xl text-foreground dark:text-white leading-none mt-2">
              Inquiries
            </h2>
          </div>
          <p className="text-xs text-muted-foreground dark:text-white/45 hidden md:block">
            Tap a tile to filter the list below.
          </p>
        </div>

        <div className={`rounded-2xl bg-card dark:bg-white/[0.06] dark:backdrop-blur-md border border-border dark:border-white/[0.08] p-4 orbit-box-dark ${darkScope}`}>
          <ServiceTabs activeTab={activeTab} onTabChange={setActiveTab} />
        </div>

        {loadingInquiries ? (
          <div className={darkScope}>
            <SkeletonStats count={5} />
          </div>
        ) : (
          <div className={darkScope}>
            <StatsRow
              stats={stats}
              activeFilter={activeStatFilter}
              onStatClick={handleStatClick}
            />
          </div>
        )}

        <div className={`rounded-2xl bg-card dark:bg-white/[0.06] dark:backdrop-blur-md border border-border dark:border-white/[0.08] p-4 orbit-box-dark ${darkScope}`}>
          <SearchFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            onManualEntry={() => setShowManualEntry(true)}
          />
        </div>

        {loadingInquiries ? (
          <div className={darkScope}>
            <SkeletonTable rows={5} />
          </div>
        ) : filteredInquiries.length === 0 && inquiries.length === 0 ? (
          <div className={`rounded-2xl bg-card dark:bg-white/[0.06] dark:backdrop-blur-md border border-border dark:border-white/[0.08] p-8 orbit-box-dark ${darkScope}`}>
            <EmptyState
              icon={Inbox}
              title="No inquiries yet"
              description="Every lead lands here. When you forget to follow up, the shoot dies quietly — share your form or add one by hand."
              actionLabel="Add a lead"
              onAction={() => setShowManualEntry(true)}
              // NOVA-NUDGE-1 — same sentence the Inquiries page
              // teaches: say it to Nova and she logs the lead via
              // her add-lead tool instead of the manual sheet.
              novaAsk="Add my first lead — Sarah, sarah@email.com, wedding inquiry"
            />
          </div>
        ) : (
          <div className={`rounded-2xl bg-card dark:bg-white/[0.06] dark:backdrop-blur-md border border-border dark:border-white/[0.08] p-2 overflow-hidden orbit-box-dark ${darkScope}`}>
            <InquiryTable
              inquiries={filteredInquiries}
              onViewDetails={setSelectedInquiry}
            />
          </div>
        )}
      </section>

      {/* ─── Hero rings — planet-bound or generic ─────────────────
          When gamification is on AND the studio has at least one
          in-progress planet, the hero ring slot becomes the planet
          they're closest to earning (HeroPlanet picks it). Otherwise
          we keep the legacy generic StudioRings.
          UX-7 — moved below the pipeline (was above it). Kept, not
          cut: the rings are the motivational layer, the pipeline is
          the work. */}
      {showHeroPlanet ? (
        <div className={darkScope}><PassportTodayCard /></div>
      ) : (
        <div className={darkScope}>
          <StudioRings />
          {/* NAV-6 — Passport left the nav bar in simplified mode,
              so the rings/goals band carries the way in.  HeroPlanet
              (the other occupant of this slot) already tap-throughs
              to /galaxy as a whole card; this small affordance
              covers the generic-rings fallback.  Hidden when
              gamification is off (Passport doesn't exist then). */}
          {gamificationOn && (
            <button
              type="button"
              onClick={() => navigate('/galaxy')}
              className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground dark:text-white/55 dark:hover:text-white/85 transition-colors"
            >
              Open Passport
              <span aria-hidden>→</span>
            </button>
          )}
        </div>
      )}

      {/* ─── Pinned goals — its own dedicated section ─────────────
          Wave 10 — Moved below the hero, no longer competing for the
          right column. Empty-goal state hides itself. */}
      <section className={darkScope}>
        <PinnedGoalsCard />
      </section>

      {/* ─── Activation card — restrained treatment ───────────────
          Wave 10 — Full-width, no glass wrapper. Uses its own card.
          UX-7 — `collapsible`: once ≥3 steps are done the nine-row
          checklist folds into a compact "Setup 4/9" pill that expands
          on click (see ActivationCard). Fresh studios (<3 done) still
          get the full checklist — it's their map. */}
      <section className={darkScope}>
        <ActivationCard collapsible />
      </section>

      {/* SUB-1 Phase 3B — quiet-by-default storage awareness on Today.
          `TodayStorageWidget` renders nothing under 75%; kicks in as
          a compact card only when the studio is approaching / over
          their storage cap. Full-fat variant lives in Settings. */}
      <TodayStorageWidget />

      {/* ─── Data-driven widgets (only when there's activity) ───
          Wave 10 — Dropped the redundant `.orbit-glass rounded-3xl p-1`
          shell around each widget. The widgets carry their own card
          treatment and the outer ring was causing the "boxes within
          boxes overlapping" visual bug. */}
      {hasStudioActivity && (
        <>
          <div className={darkScope}>
            <RecentProjects />
          </div>
          <div className={darkScope}>
            <AnalyticsOverview analytics={analytics} />
          </div>
          <div className={darkScope}>
            <SummaryPanels summaries={summaries} />
          </div>
        </>
      )}

      {/* ─── Modals ──────────────────────────────────────────────── */}
      <InquiryModal
        inquiry={selectedInquiry}
        open={!!selectedInquiry}
        onOpenChange={(open) => !open && setSelectedInquiry(null)}
        onUpdate={handleUpdateInquiry}
        onDelete={handleDeleteInquiry}
      />
      {showManualEntry && (
        <ManualEntryModal
          onClose={() => setShowManualEntry(false)}
          defaultServiceType={activeTab}
        />
      )}
      <CreateProjectModal
        open={createProjectOpen}
        onOpenChange={setCreateProjectOpen}
      />
      {/* POL-2 — new-gallery flow reused from the Galleries page. */}
      <NewGalleryDialog
        open={newGalleryOpen}
        onClose={() => setNewGalleryOpen(false)}
      />

      </div>
    </div>
  );
}

// ─── Shortcut tile ──────────────────────────────────────────────
// POL-16 — Pixieset-style multi-tone accents. Wave 10 had unified
// everything to orbit-orange; Rennan reversed that call (Jul 2026)
// after landing on Pixieset's product grid as the reference — each
// action gets its own identity on the shared neutral glass surface.
// The tone touches ONLY the icon chip so the row still reads calm.
//
// BRAND-1 (Aug 2026) — the pastels used to be stock Tailwind
// (sky/teal/emerald/amber/red/pink). They're now the 1960s
// space-race accents from index.css. Same architecture, same keys,
// same call sites: only the colour family moved. Still deliberately
// NOT orange-for-everything — more so now, because each accent
// carries a ROLE (blue = brand/today, teal = calendar/in-progress,
// phosphor = money, gold = goals/pending, red = overdue, rose =
// clients), so a tile's colour is a hint about what it does.
//
// Two tokens per accent and they are NOT interchangeable:
//   • `orbit-*`      — the READABLE tone. Theme-aware, so it flips
//                      dark-on-light / light-on-dark by itself.
//                      That's why there are no `dark:` variants here.
//   • `orbit-*-fill` — the CHIP FILL tone. Identical in both themes,
//                      because the chip sits on glass either way.
// Keys stay generic (`green`, `yellow`, `pink`) — call sites pass
// them by name and renaming would touch every shortcut.
type TileTone = 'teal' | 'blue' | 'red' | 'green' | 'yellow' | 'pink';

const TILE_TONES: Record<TileTone, { chip: string; icon: string }> = {
  teal:   { chip: 'bg-orbit-teal-fill/[0.22] border-orbit-teal-fill/[0.42]',         icon: 'text-orbit-teal' },
  blue:   { chip: 'bg-orbit-blue-fill/[0.22] border-orbit-blue-fill/[0.42]',         icon: 'text-orbit-blue' },
  red:    { chip: 'bg-orbit-red-fill/[0.22] border-orbit-red-fill/[0.42]',           icon: 'text-orbit-red' },
  green:  { chip: 'bg-orbit-phosphor-fill/[0.22] border-orbit-phosphor-fill/[0.42]', icon: 'text-orbit-phosphor' },
  yellow: { chip: 'bg-orbit-gold-fill/[0.22] border-orbit-gold-fill/[0.42]',         icon: 'text-orbit-gold' },
  pink:   { chip: 'bg-orbit-rose-fill/[0.22] border-orbit-rose-fill/[0.42]',         icon: 'text-orbit-rose' },
};

function ShortcutTile({
  icon: Icon,
  label,
  tone = 'teal',
  visual,
  onClick,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  tone?: TileTone;
  /** POL-16 — optional replacement for the icon chip (e.g. Nova). */
  visual?: React.ReactNode;
  onClick: () => void;
}) {
  const t = TILE_TONES[tone];
  return (
    <button
      onClick={onClick}
      className="group flex items-center gap-3 px-4 py-3.5 rounded-2xl bg-card dark:bg-white/[0.06] dark:backdrop-blur-md hover:bg-secondary dark:hover:bg-white/[0.10] border border-border dark:border-white/[0.08] dark:hover:border-white/[0.16] transition-colors text-left orbit-box-dark"
    >
      {visual ?? (
        <span className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 transition-transform group-hover:scale-110 border ${t.chip}`}>
          <Icon className={`w-4 h-4 ${t.icon}`} />
        </span>
      )}
      <span className="text-sm font-semibold text-foreground dark:text-white/90 leading-tight">
        {label}
      </span>
    </button>
  );
}
// Wave 10 — RollupColumn removed with the hero rollup band.

/**
 * TodayStorageWidget — quiet-by-default storage awareness on Today.
 *
 * SUB-1 Phase 3B. Renders nothing under 75% of the cap so the
 * dashboard stays calm for the 95% of studios who aren't near it.
 * Kicks in as the compact `<StorageUsageBar />` at 75-89%, and the
 * same compact bar carries the "More space is coming your way…"
 * copy + upgrade nudge at 90%+ (the compact variant embeds the
 * UpgradeNudgeCard itself when isAtLimit fires).
 *
 * Free plan is hidden here — free studios see the activation flow
 * above, not a storage bar. Business tier is unlimited so no need.
 */
function TodayStorageWidget() {
  const { plan, storage, isLoading } = useUsageSnapshot();
  if (isLoading) return null;
  if (plan === 'free') return null;
  if (storage.isUnlimited) return null;
  if (!storage.isNearLimit) return null;
  return (
    <section>
      <StorageUsageBar compact />
    </section>
  );
}
